'use client'

import { Badge, LinkBlock } from '@cleanlab/design-system/components'
import {
  IconArrowUpRight,
  IconFolders,
  IconSettings,
  IconUser,
} from '@cleanlab/design-system/icons'
import { cn } from '@cleanlab/design-system/utils'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { type ReactNode } from 'react'

import { CODEX_DOCS_URL } from '@/components/LandingPage/consts'
import {
  SideNavAccordion,
  SideNavAccordionContent,
  SideNavAccordionItem,
  SideNavAccordionTrigger,
} from '@/components/SideNav/SideNavAccordion'
import { APP_PATHS, PAGE_TITLES } from '@/global/consts'
import { useUser } from '@/providers/account/UserContextProvider'
import { analytics } from '@/services/analytics'

import ProjectNav, { ProjectNavLabel } from './components/ProjectNav'

const navItems: {
  href: (typeof APP_PATHS)[keyof typeof APP_PATHS]
  label: string
  icon?: ReactNode
}[] = [
  {
    href: APP_PATHS.ACCOUNT,
    label: PAGE_TITLES.ACCOUNT,
    icon: <IconUser size={14} />,
  },
  { href: APP_PATHS.PROJECTS, label: PAGE_TITLES.PROJECTS, icon: <IconFolders size={14} /> },
]

const adminNavItems: { href: (typeof APP_PATHS)[keyof typeof APP_PATHS]; label: string }[] = [
  { href: APP_PATHS.ADMIN_ORGANIZATIONS, label: PAGE_TITLES.ADMIN_ORGANIZATIONS },
  { href: APP_PATHS.ADMIN_USERS, label: PAGE_TITLES.ADMIN_USERS },
  { href: APP_PATHS.ADMIN_CLUSTER_RANKS, label: PAGE_TITLES.ADMIN_CLUSTER_RANKS },
  { href: APP_PATHS.ADMIN_PROJECT_CLUSTERING, label: PAGE_TITLES.ADMIN_PROJECT_CLUSTERING },
]

const baseNavLinkClass =
  'type-body-100 relative rounded-2 p-4 outline-0 transition-colors focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus'

export function SideNav() {
  const params = useParams()
  const projectId = params.projectId as string
  const pathname = usePathname() as (typeof APP_PATHS)[keyof typeof APP_PATHS]
  const { userData, isCleanlabAdmin } = useUser()
  const isAccountVerified = userData?.email_verified && userData?.is_account_activated
  const isProjectPage = !!projectId

  if (!isAccountVerified) {
    return null
  }

  const defaultAccordionValues = ['project-nav', 'admin-panel']

  return (
    <nav className="flex h-full w-[240px] shrink-0 flex-col justify-start overflow-y-auto border-r border-border-1">
      <div className="flex flex-col gap-[10px] px-7 py-5">
        <div className="flex flex-col gap-4">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                baseNavLinkClass,
                'text-text-primary',
                pathname === item.href
                  ? 'bg-surface-0-active'
                  : 'hover:bg-surface-0-hover hover:text-text-faint'
              )}
            >
              <div className="flex items-center gap-4">
                {item.icon}
                <p className="type-body-100">{item.label}</p>
                {item.label === PAGE_TITLES.ACCOUNT && isCleanlabAdmin && (
                  <Badge hue="blue" size="small">
                    Admin
                  </Badge>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
      <SideNavAccordion defaultValue={defaultAccordionValues}>
        {isProjectPage && (
          <SideNavAccordionItem className="bg-surface-1" value="project-nav">
            <SideNavAccordionTrigger>
              <ProjectNavLabel />
            </SideNavAccordionTrigger>
            <SideNavAccordionContent>
              <ProjectNav />
            </SideNavAccordionContent>
          </SideNavAccordionItem>
        )}
        {isCleanlabAdmin && (
          <SideNavAccordionItem value="admin-panel">
            <SideNavAccordionTrigger>
              <div className="flex items-center gap-4">
                <IconSettings size={14} />
                Admin Panel
              </div>
            </SideNavAccordionTrigger>
            <SideNavAccordionContent>
              <div className="flex flex-col gap-[10px]">
                {adminNavItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      baseNavLinkClass,
                      'text-text-faint',
                      pathname === item.href || pathname.startsWith(`${item.href}/`)
                        ? 'bg-surface-0-active'
                        : 'hover:bg-surface-0-hover hover:text-text-faint'
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </SideNavAccordionContent>
          </SideNavAccordionItem>
        )}
      </SideNavAccordion>
      <hr className="w-full border-border-1" />
      <div className="border-b border-border-1 p-7">
        <LinkBlock
          className="type-body-100 px-4 text-text-primary"
          variant="secondary"
          target="_blank"
          href={CODEX_DOCS_URL}
          iconEnd={<IconArrowUpRight />}
          onClick={() => analytics.track('user_click_docs')}
        >
          Docs
        </LinkBlock>
      </div>
    </nav>
  )
}
